import { limpiarMarcas } from './transcripcion';

/**
 * Máquina de estados de la sesión de entrevista (D-10 de la tanda 2).
 *
 * Reducer **puro**: no toca red, audio, avatar ni timers. Recibe eventos y
 * devuelve el estado siguiente. Los efectos (socket, micrófono,
 * reproducción, temporizadores) viven en el orquestador, que traduce lo
 * que pasa afuera a eventos de este módulo. Así la lógica de "qué se puede
 * hacer ahora" se prueba sin DOM.
 *
 * Invariante central: **nada queda colgado**. Todo estado de espera tiene
 * un evento que lo destraba (respuesta del servidor, fin de reproducción,
 * o un timeout que el orquestador dispara con las constantes de abajo).
 */

// ==============================
// Constantes con nombre
// ==============================

/** Sin `paciente.hablando_fin` ni `turno.fallido` en este plazo, se re-arma el PTT. */
export const ESPERA_RESPUESTA_MS = 45_000;
/**
 * Llegó `paciente.hablando_fin` pero no el audio: se re-arma el PTT tras
 * este plazo. Sin esto, un binario perdido deja el PTT deshabilitado para
 * siempre (decisión 3 del FRENO 1 de la tanda 2).
 */
export const ESPERA_AUDIO_PACIENTE_MS = 10_000;
/** Primer `conectar` sin ack en este plazo → reconexión fallida con "Reintentar". */
export const ESPERA_CONEXION_MS = 20_000;
/**
 * Respaldo de la reconexión: si en este plazo no volvió el ack de
 * `conectar`, se declara la reconexión fallida y se ofrece "Reintentar".
 * Es mayor que la ventana de reintentos de Socket.IO (~45 s, ver
 * `RECONEXION_INTENTOS` en `api-client`), que normalmente avisa antes; existe
 * para que "Reconectando…" no quede colgado si ese aviso no llega, como
 * pasó en la prueba desde `sp.mvp` del 2026-09-19.
 */
export const ESPERA_RECONEXION_MS = 60_000;
/** Tope de grabación: al vencer, se corta y se envía lo grabado (D-6). */
export const TOPE_GRABACION_MS = 60_000;
/** Duración de los avisos no bloqueantes (D-10). */
export const DURACION_AVISO_MS = 5_000;

// ==============================
// Estado
// ==============================

export type Fase =
  /** Antes de empezar: botón "Iniciar entrevista" o "Retomar entrevista". */
  | 'preparada'
  /** Verificando audio, pidiendo micrófono, ticket y conexión. */
  | 'iniciando'
  /** `AudioDeclaradoError`: navegador no compatible (D-9). Terminal. */
  | 'no_compatible'
  /** Falla antes de conectar (micrófono, ticket, token). Se puede reintentar. */
  | 'inicio_fallido'
  | 'en_curso'
  /** Socket caído a mitad de sesión; Socket.IO reintenta solo. */
  | 'reconectando'
  /** Socket.IO se rindió o el primer ack no llegó: botón "Reintentar". */
  | 'reconexion_fallida'
  /** Llegó `sesion.error`: el servidor cierra el socket. */
  | 'error_sesion'
  | 'finalizando'
  /** `/finalizar` falló: se puede reintentar solo el REST. */
  | 'cierre_fallido'
  | 'finalizada';

export type EtapaTurno =
  /** PTT armado. */
  | 'libre'
  | 'grabando'
  /** Grabación detenida: midiendo RMS y enviando. */
  | 'procesando'
  /** Turno enviado: esperando al paciente. */
  | 'esperando';

export interface Turno {
  /** Número del turno en curso o del último enviado. Invalida timeouts viejos. */
  numero: number;
  etapa: EtapaTurno;
  /** Llegó `paciente.hablando_inicio` para este turno. */
  pacienteHablando: boolean;
  /** Llegó `paciente.hablando_fin` para este turno. */
  pacienteTermino: boolean;
  /** Llegó el `audio.binario` del paciente para este turno. */
  audioRecibido: boolean;
}

export type CodigoAviso =
  | 'audio_vacio'
  | 'sin_respuesta'
  | 'turno_fallido'
  | 'audio_no_llego'
  | 'grabacion_fallida';

export interface Aviso {
  codigo: CodigoAviso;
  /** Identifica esta aparición: `AVISO_EXPIRADO` solo borra el aviso con este id. */
  id: number;
}

export interface LineaTranscripcion {
  rol: 'alumno' | 'paciente';
  texto: string;
}

export interface EstadoSesion {
  fase: Fase;
  /** Cómo se entró: sesión nueva (con ticket) o retomando una en curso. */
  modo: 'nuevo' | 'retomar';
  simulacionId: string | null;
  turno: Turno;
  /** Audio del paciente sonando ahora. El PTT no se arma mientras suene (D-6). */
  reproduciendo: boolean;
  /** La reproducción falló (autoplay): se muestra "Reproducir" (D-8). */
  reproduccionBloqueada: boolean;
  transcripcion: LineaTranscripcion[];
  /** Expresión aplicada al avatar en el último turno del paciente. */
  expresion: string;
  aviso: Aviso | null;
  /** Código del `sesion.error`, o motivo de `inicio_fallido` / `cierre_fallido`. */
  errorCodigo: string | null;
  /** El usuario pidió finalizar y falta que confirme. */
  confirmandoCierre: boolean;
  /** Contador para ids de aviso. */
  siguienteAvisoId: number;
}

const TURNO_INICIAL: Turno = {
  numero: 0,
  etapa: 'libre',
  pacienteHablando: false,
  pacienteTermino: false,
  audioRecibido: false,
};

export function estadoInicial(modo: 'nuevo' | 'retomar' = 'nuevo'): EstadoSesion {
  return {
    fase: 'preparada',
    modo,
    simulacionId: null,
    turno: TURNO_INICIAL,
    reproduciendo: false,
    reproduccionBloqueada: false,
    transcripcion: [],
    expresion: 'NEUTRAL',
    aviso: null,
    errorCodigo: null,
    confirmandoCierre: false,
    siguienteAvisoId: 1,
  };
}

// ==============================
// Eventos
// ==============================

export type EventoSesion =
  // Inicio
  | { tipo: 'INICIAR' }
  | { tipo: 'AUDIO_NO_SOPORTADO' }
  | { tipo: 'INICIO_FALLIDO'; motivo: string }
  | { tipo: 'CONECTADO'; simulacionId: string }
  | { tipo: 'TIMEOUT_CONEXION' }
  /**
   * Retomar recibió `ticket_invalido`: se pasa a sesión nueva y el
   * orquestador pide un ticket, una sola vez (decisión de la tanda 2).
   */
  | { tipo: 'RETOMAR_FALLIDO' }
  // Conexión
  | { tipo: 'DESCONECTADO' }
  | { tipo: 'RECONEXION_FALLIDA' }
  | { tipo: 'REINTENTAR_CONEXION' }
  // Turno del alumno
  | { tipo: 'PTT_INICIAR' }
  | { tipo: 'PTT_DETENER' }
  | { tipo: 'GRABACION_FALLIDA' }
  | { tipo: 'GRABACION_DESCARTADA' }
  | { tipo: 'TURNO_ENVIADO' }
  // Turno del paciente
  | { tipo: 'PACIENTE_HABLANDO_INICIO' }
  | { tipo: 'AUDIO_PACIENTE'; alumno: string; paciente: string; expresion: string }
  | { tipo: 'AUDIO_RECIBIDO' }
  | { tipo: 'REPRODUCCION_TERMINADA' }
  | { tipo: 'REPRODUCCION_BLOQUEADA' }
  | { tipo: 'REPRODUCCION_MANUAL' }
  | { tipo: 'PACIENTE_HABLANDO_FIN' }
  | { tipo: 'TURNO_FALLIDO' }
  | { tipo: 'TIMEOUT_RESPUESTA'; turno: number }
  | { tipo: 'TIMEOUT_AUDIO'; turno: number }
  | { tipo: 'AVISO_EXPIRADO'; id: number }
  // Errores y cierre
  | { tipo: 'SESION_ERROR'; codigo: string }
  | { tipo: 'CIERRE_SOLICITADO' }
  | { tipo: 'CIERRE_CANCELADO' }
  | { tipo: 'CIERRE_CONFIRMADO' }
  | { tipo: 'CIERRE_FALLIDO'; motivo: string }
  | { tipo: 'REINTENTAR_CIERRE' }
  | { tipo: 'FINALIZADA' }
  /** Volver al estado inicial ("volver al Paso 3" tras `ticket_invalido`, reintento de inicio). */
  | { tipo: 'REINICIAR'; modo: 'nuevo' | 'retomar' };

// ==============================
// Selectores
// ==============================

/** El PTT se puede apretar para empezar a grabar. */
export function pttArmado(s: EstadoSesion): boolean {
  return s.fase === 'en_curso' && s.turno.etapa === 'libre' && !s.reproduciendo;
}

/** El PTT se puede apretar para terminar la grabación. */
export function pttGrabando(s: EstadoSesion): boolean {
  return s.fase === 'en_curso' && s.turno.etapa === 'grabando';
}

/**
 * El turno está en proceso: la pregunta del alumno se está enviando o se
 * espera la respuesta, y todavía no llegó el audio. Es lo que el avatar
 * dibuja como "pensando" (tanda 8, pieza 5). Es estado del turno, no una
 * emoción: termina cuando empieza el pre-roll, al recibir el audio.
 */
export function turnoEnProceso(s: EstadoSesion): boolean {
  return (
    s.fase === 'en_curso' &&
    (s.turno.etapa === 'procesando' || (s.turno.etapa === 'esperando' && !s.turno.audioRecibido))
  );
}

/** Hay una conexión viva o por recuperarse: fases desde las que se puede finalizar. */
export function sesionActiva(s: EstadoSesion): boolean {
  return s.fase === 'en_curso' || s.fase === 'reconectando' || s.fase === 'reconexion_fallida';
}

// ==============================
// Reducer
// ==============================

export function reducirSesion(s: EstadoSesion, e: EventoSesion): EstadoSesion {
  switch (e.tipo) {
    // ---------- Inicio ----------
    case 'INICIAR':
      if (s.fase !== 'preparada' && s.fase !== 'inicio_fallido') return s;
      return { ...s, fase: 'iniciando', errorCodigo: null };

    case 'AUDIO_NO_SOPORTADO':
      return s.fase === 'iniciando' ? { ...s, fase: 'no_compatible' } : s;

    case 'INICIO_FALLIDO':
      return s.fase === 'iniciando' ? { ...s, fase: 'inicio_fallido', errorCodigo: e.motivo } : s;

    case 'CONECTADO':
      if (s.fase !== 'iniciando' && s.fase !== 'reconectando' && s.fase !== 'reconexion_fallida') {
        return s;
      }
      // Al (re)conectar, el PTT se re-arma: lo que estuviera en vuelo se
      // perdió con el socket (D-10).
      return {
        ...s,
        fase: 'en_curso',
        simulacionId: e.simulacionId,
        turno: { ...TURNO_INICIAL, numero: s.turno.numero },
      };

    case 'RETOMAR_FALLIDO':
      return s.fase === 'iniciando' && s.modo === 'retomar' ? { ...s, modo: 'nuevo' } : s;

    case 'TIMEOUT_CONEXION':
      return s.fase === 'iniciando' ? { ...s, fase: 'reconexion_fallida' } : s;

    // ---------- Conexión ----------
    case 'DESCONECTADO':
      return s.fase === 'en_curso' ? { ...s, fase: 'reconectando', confirmandoCierre: false } : s;

    case 'RECONEXION_FALLIDA':
      return s.fase === 'reconectando' || s.fase === 'iniciando'
        ? { ...s, fase: 'reconexion_fallida' }
        : s;

    case 'REINTENTAR_CONEXION':
      return s.fase === 'reconexion_fallida' ? { ...s, fase: 'reconectando' } : s;

    // ---------- Turno del alumno ----------
    case 'PTT_INICIAR':
      return pttArmado(s) ? { ...s, turno: { ...s.turno, etapa: 'grabando' }, aviso: null } : s;

    case 'PTT_DETENER':
      return pttGrabando(s) ? { ...s, turno: { ...s.turno, etapa: 'procesando' } } : s;

    case 'GRABACION_FALLIDA':
      if (s.turno.etapa !== 'grabando' && s.turno.etapa !== 'procesando') return s;
      return conAviso({ ...s, turno: { ...s.turno, etapa: 'libre' } }, 'grabacion_fallida');

    case 'GRABACION_DESCARTADA':
      if (s.turno.etapa !== 'procesando') return s;
      return conAviso({ ...s, turno: { ...s.turno, etapa: 'libre' } }, 'audio_vacio');

    case 'TURNO_ENVIADO':
      if (s.turno.etapa !== 'procesando') return s;
      return {
        ...s,
        turno: { ...TURNO_INICIAL, numero: s.turno.numero + 1, etapa: 'esperando' },
      };

    // ---------- Turno del paciente ----------
    case 'PACIENTE_HABLANDO_INICIO':
      return s.turno.etapa === 'esperando'
        ? { ...s, turno: { ...s.turno, pacienteHablando: true } }
        : s;

    case 'AUDIO_PACIENTE': {
      const lineas: LineaTranscripcion[] = [];
      if (e.alumno.trim() !== '') lineas.push({ rol: 'alumno', texto: e.alumno });
      // Las marcas de actuación ("[suspiro]") no se muestran al alumno.
      const paciente = limpiarMarcas(e.paciente);
      if (paciente !== '') lineas.push({ rol: 'paciente', texto: paciente });
      return { ...s, transcripcion: [...s.transcripcion, ...lineas], expresion: e.expresion };
    }

    case 'AUDIO_RECIBIDO':
      return cerrarTurnoSiCorresponde({
        ...s,
        reproduciendo: true,
        reproduccionBloqueada: false,
        turno: s.turno.etapa === 'esperando' ? { ...s.turno, audioRecibido: true } : s.turno,
      });

    case 'REPRODUCCION_TERMINADA':
      return cerrarTurnoSiCorresponde({ ...s, reproduciendo: false, reproduccionBloqueada: false });

    case 'REPRODUCCION_BLOQUEADA':
      // Nada suena: el PTT puede re-armarse; el botón "Reproducir" queda
      // disponible (D-8).
      return cerrarTurnoSiCorresponde({ ...s, reproduciendo: false, reproduccionBloqueada: true });

    case 'REPRODUCCION_MANUAL':
      return s.reproduccionBloqueada
        ? { ...s, reproduciendo: true, reproduccionBloqueada: false }
        : s;

    case 'PACIENTE_HABLANDO_FIN':
      if (s.turno.etapa !== 'esperando') return s;
      return cerrarTurnoSiCorresponde({ ...s, turno: { ...s.turno, pacienteTermino: true } });

    case 'TURNO_FALLIDO':
      if (s.fase !== 'en_curso') return s;
      return conAviso(
        s.turno.etapa === 'esperando' ? { ...s, turno: { ...s.turno, etapa: 'libre' } } : s,
        'turno_fallido',
      );

    case 'TIMEOUT_RESPUESTA':
      if (s.turno.etapa !== 'esperando' || s.turno.numero !== e.turno || s.turno.pacienteTermino) {
        return s;
      }
      return conAviso({ ...s, turno: { ...s.turno, etapa: 'libre' } }, 'sin_respuesta');

    case 'TIMEOUT_AUDIO':
      if (
        s.turno.etapa !== 'esperando' ||
        s.turno.numero !== e.turno ||
        !s.turno.pacienteTermino ||
        s.turno.audioRecibido
      ) {
        return s;
      }
      return conAviso({ ...s, turno: { ...s.turno, etapa: 'libre' } }, 'audio_no_llego');

    case 'AVISO_EXPIRADO':
      return s.aviso?.id === e.id ? { ...s, aviso: null } : s;

    // ---------- Errores y cierre ----------
    case 'SESION_ERROR':
      if (s.fase === 'finalizando' || s.fase === 'finalizada' || s.fase === 'cierre_fallido') {
        return s;
      }
      return {
        ...s,
        fase: 'error_sesion',
        errorCodigo: e.codigo,
        confirmandoCierre: false,
        turno: { ...s.turno, etapa: 'libre' },
      };

    case 'CIERRE_SOLICITADO':
      return sesionActiva(s) ? { ...s, confirmandoCierre: true } : s;

    case 'CIERRE_CANCELADO':
      return { ...s, confirmandoCierre: false };

    case 'CIERRE_CONFIRMADO':
      if (!s.confirmandoCierre || !sesionActiva(s)) return s;
      return { ...s, fase: 'finalizando', confirmandoCierre: false, aviso: null };

    case 'CIERRE_FALLIDO':
      return s.fase === 'finalizando' ? { ...s, fase: 'cierre_fallido', errorCodigo: e.motivo } : s;

    case 'REINTENTAR_CIERRE':
      return s.fase === 'cierre_fallido' ? { ...s, fase: 'finalizando', errorCodigo: null } : s;

    case 'REINICIAR':
      return s.fase === 'error_sesion' ||
        s.fase === 'inicio_fallido' ||
        s.fase === 'reconexion_fallida'
        ? estadoInicial(e.modo)
        : s;

    case 'FINALIZADA':
      return s.fase === 'finalizando' ? { ...s, fase: 'finalizada' } : s;
  }
}

/**
 * Re-arma el PTT cuando el turno del paciente terminó de verdad: llegó
 * `paciente.hablando_fin`, llegó su audio y ya no suena (D-6). En un
 * notebook con parlantes, re-armar antes graba la voz del paciente.
 */
function cerrarTurnoSiCorresponde(s: EstadoSesion): EstadoSesion {
  const t = s.turno;
  if (t.etapa === 'esperando' && t.pacienteTermino && t.audioRecibido && !s.reproduciendo) {
    return { ...s, turno: { ...t, etapa: 'libre' } };
  }
  return s;
}

function conAviso(s: EstadoSesion, codigo: CodigoAviso): EstadoSesion {
  return {
    ...s,
    aviso: { codigo, id: s.siguienteAvisoId },
    siguienteAvisoId: s.siguienteAvisoId + 1,
  };
}
